import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import ServiceTemplateService from '../services/serviceTemplateService';
import PageHeader from '../../../components/common/PageHeader';
import SearchHeader from '../../../components/common/SearchHeader';
import PaginationComponent from '../../../components/common/PaginationComponent';
import FormBuilder from '../../../components/common/FormBuilder';
import DeleteConfirmDialog from '../../../components/common/DeleteConfirmDialog';
import ServiceTemplateTable from '../components/ServiceTemplateTable';
import { createServiceTemplateSchema, updateServiceTemplateSchema } from '../schemas/serviceTemplateFormSchema';

const ServiceTemplateListingPage = () => {
  // Data states
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalTemplates, setTotalTemplates] = useState(0);

  // Sorting states
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Load templates function
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      };
      
      // Server-side filtering
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      if (statusFilter === 'active') {
        params.active_only = true;
      } else if (statusFilter === 'inactive') {
        params.active_only = false;
      }
      
      if (serviceTypeFilter !== 'all') {
        params.service_type = serviceTypeFilter;
      }
      
      // Server-side sorting
      params.sort_by = sortField;
      params.sort_order = sortDirection;
      
      const result = await ServiceTemplateService.getAll(params);
      
      if (result.success) {
        setTemplates(result.data || []);
        setTotalTemplates(result.total || result.data?.length || 0);
      } else {
        setError(result.error);
        setTemplates([]);
        setTotalTemplates(0);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Σφάλμα φόρτωσης προτύπων υπηρεσιών');
      setTemplates([]);
      setTotalTemplates(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, statusFilter, serviceTypeFilter, sortField, sortDirection]);

  // Load data on mount and when filters change
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Handle actions
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleCreate = () => {
    setSelectedTemplate(null);
    setCreateModalOpen(true);
  };

  const handleEdit = (template) => {
    const editableTemplate = {
      ...template,
      features: Array.isArray(template.features) && template.features.length > 0
        ? template.features.join('\n')
        : ''
    };
    setSelectedTemplate(editableTemplate);
    setEditModalOpen(true);
  };

  const handleDelete = (template) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Form submission handlers
  const formatTemplatePayload = (formData) => {
    const allowedFields = [
      'name',
      'description',
      'service_type',
      'default_price',
      'billing_period',
      'vat_rate',
      'is_active',
      'features'
    ];

    const payload = allowedFields.reduce((acc, field) => {
      if (formData[field] !== undefined) {
        acc[field] = formData[field];
      }
      return acc;
    }, {});

    if (typeof payload.default_price === 'string') {
      const parsedPrice = parseFloat(payload.default_price);
      payload.default_price = Number.isNaN(parsedPrice) ? 0 : parsedPrice;
    }

    if (typeof payload.vat_rate === 'string') {
      const parsedVat = parseFloat(payload.vat_rate);
      if (payload.vat_rate.trim() === '') {
        delete payload.vat_rate;
      } else {
        payload.vat_rate = Number.isNaN(parsedVat) ? undefined : parsedVat;
      }
    }

    if (payload.vat_rate === undefined) {
      delete payload.vat_rate;
    }

    if (typeof payload.features === 'string') {
      payload.features = payload.features
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    }

    if (!Array.isArray(payload.features)) {
      payload.features = [];
    }

    if (payload.billing_period === '') {
      delete payload.billing_period;
    }

    if (payload.description === '') {
      payload.description = null;
    }

    return payload;
  };

  const handleCreateSubmit = async (formData) => {
    try {
      const result = await ServiceTemplateService.create(formatTemplatePayload(formData));
      if (result.success) {
        setCreateModalOpen(false);
        loadTemplates();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: 'Σφάλμα δημιουργίας προτύπου υπηρεσίας' };
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      const result = await ServiceTemplateService.update(
        selectedTemplate.id,
        formatTemplatePayload(formData)
      );
      if (result.success) {
        setEditModalOpen(false);
        loadTemplates();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: 'Σφάλμα ενημέρωσης προτύπου υπηρεσίας' };
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await ServiceTemplateService.delete(selectedTemplate.id);
      if (result.success) {
        setDeleteDialogOpen(false);
        loadTemplates();
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Σφάλμα διαγραφής προτύπου υπηρεσίας');
    }
  };

  // Custom search header with additional filters
  const renderSearchHeader = () => (
    <Box sx={{ mb: 3 }}>
      <SearchHeader
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Αναζήτηση προτύπων (όνομα, περιγραφή)..."
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        statusOptions={[
          { value: 'all', label: 'Όλα τα πρότυπα' },
          { value: 'active', label: 'Ενεργά' },
          { value: 'inactive', label: 'Ανενεργά' }
        ]}
      />
      
      {/* Additional Filters */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Τύπος Υπηρεσίας</InputLabel>
          <Select
            value={serviceTypeFilter}
            onChange={(e) => setServiceTypeFilter(e.target.value)}
            label="Τύπος Υπηρεσίας"
          >
            <MenuItem value="all">Όλοι</MenuItem>
            <MenuItem value="development">Ανάπτυξη</MenuItem>
            <MenuItem value="hosting">Hosting</MenuItem>
            <MenuItem value="maintenance">Συντήρηση</MenuItem>
            <MenuItem value="design">Σχεδιασμός</MenuItem>
            <MenuItem value="domain">Domain</MenuItem>
            <MenuItem value="application">Εφαρμογή (SaaS)</MenuItem>
            <MenuItem value="support">Υποστήριξη</MenuItem>
            <MenuItem value="seo">SEO</MenuItem>
            <MenuItem value="consulting">Σύμβουλος</MenuItem>
            <MenuItem value="training">Εκπαίδευση</MenuItem>
            <MenuItem value="license">Άδεια Λογισμικού</MenuItem>
            <MenuItem value="other">Άλλο</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  // Page header actions
  const pageActions = [
    <IconButton
      key="create"
      color="primary"
      onClick={handleCreate}
      sx={{ 
        backgroundColor: 'primary.main',
        color: 'white',
        '&:hover': {
          backgroundColor: 'primary.dark',
        }
      }}
    >
      <AddIcon />
    </IconButton>
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <PageHeader
        title="Διαχείριση Προτύπων Υπηρεσιών"
        subtitle="Δημιουργία και διαχείριση προτύπων για γρήγορη δημιουργία υπηρεσιών"
        icon="📋"
        actions={pageActions}
      />

      {/* Search and Filters */}
      {renderSearchHeader()}

      {/* Error Display */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </Typography>
        </Box>
      )}

      {/* Data Table */}
      <ServiceTemplateTable
        templates={templates}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
      />

      {/* Pagination */}
      <PaginationComponent
        currentPage={currentPage}
        totalPages={Math.ceil(totalTemplates / itemsPerPage)}
        totalItems={totalTemplates}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Create Modal */}
      <FormBuilder
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        schema={createServiceTemplateSchema}
        onSubmit={handleCreateSubmit}
        modalProps={{
          title: 'Δημιουργία Νέου Προτύπου Υπηρεσίας',
          maxWidth: 'md'
        }}
      />

      {/* Edit Modal */}
      <FormBuilder
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        schema={updateServiceTemplateSchema}
        entity={selectedTemplate}
        onSubmit={handleEditSubmit}
        modalProps={{
          title: 'Επεξεργασία Προτύπου Υπηρεσίας',
          maxWidth: 'md'
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Διαγραφή Προτύπου Υπηρεσίας"
        message={`Είστε σίγουροι ότι θέλετε να διαγράψετε το πρότυπο "${selectedTemplate?.name}"?`}
        confirmText="Διαγραφή"
        cancelText="Ακύρωση"
      />
    </Box>
  );
};

export default ServiceTemplateListingPage;
